import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, computed, signal } from '@angular/core';
import { TooltipDirective } from 'shared/directives/tooltip.directive';

/**
 * @description
 * The ProgressBarComponent displays a progress indicator with optional animation, tooltip, and custom styling.
 * It supports three modes:
 *   - "default": displays a standard progress bar.
 *   - "custom": allows custom colors for the track and fill. In this mode, the 'lightColor' and 'darkColor'
 *               inputs define the colors, and if 'solid' is true, the fill is rendered as a solid color;
 *               otherwise, a gradient is used.
 *   - "temperature": adjusts its appearance based on the progress percentage using predefined color classes.
 *
 * The component animates progress changes unless the 'instant' input is set to true.
 *
 * @param instant
 * If true, the progress updates immediately without animation.
 *
 * @param mode
 * The display mode of the progress bar: 'default', 'custom', or 'temperature'.
 *
 * @param lightColor
 * In custom mode, the color used for the track (or as the start color of a gradient).
 *
 * @param darkColor
 * In custom mode, the color used for the fill (or as the end color of a gradient).
 *
 * @param solid
 * In custom mode, if true, a solid fill color is used instead of a gradient for the bar color.
 *
 * @param label
 * An optional label to display above the progress bar.
 *
 * @param showProgressValue
 * If true, the progress percentage is displayed as a tooltip over the indicator.
 *
 * @param progress
 * The progress value (0–100) to display.
 *
 * @param animationSpeed
 * Controls the speed of the progress animation: 'slow', 'normal', or 'fast'.
 * The duration of the animation is proportional to the change in progress value.
 *
 * @param showProgressLabel
 * If true, the progress percentage is displayed above the progress bar.
 * If a label is provided, it will be shown next to it
 *
 * @example
 * <app-progress-bar [instant]="true" mode="custom" [lightColor]="'green'" [darkColor]="'blue'" [solid]="true"
 *    label="Custom Solid Colors" [progress]="80" animationSpeed="slow"></app-progress-bar>
 *
 * @example
 * <app-progress-bar mode="temperature" [progress]="75" animationSpeed="fast"></app-progress-bar>
 */
@Component({
    selector: 'app-progress-bar',
    templateUrl: './progress-bar.component.html',
    styleUrls: ['./progress-bar.component.scss'],
    imports: [CommonModule, TooltipDirective],
})
export class ProgressBarComponent implements OnChanges {
    @Input() instant = false;
    @Input() mode: 'default' | 'custom' | 'temperature' = 'default';
    @Input() lightColor?: string;
    @Input() darkColor?: string;
    @Input() solid: boolean = false;
    @Input() label?: string;
    @Input() showProgressValue = false;
    @Input() progress: number = 0;
    @Input() animationSpeed: 'slow' | 'normal' | 'fast' = 'normal';
    @Input() showProgressLabel = false;

    private animatedValue = signal(0);
    animatedPercentage = computed(() => this.animatedValue());

    animationDuration = signal(0);

    progressStyle = computed(() => ({
        width: `${this.animatedPercentage()}%`,
        'transition-duration': `${this.animationDuration()}ms`,
        'transition-property': 'width',
        'transition-timing-function': 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    }));

    private readonly ANIMATION_DURATIONS = {
        slow: 1000,
        normal: 600,
        fast: 300,
    };

    private temperatureClass = computed(() => {
        const value = this.animatedPercentage();

        switch (true) {
            case value < 20:
                return 'progress-danger';
            case value < 40:
                return 'progress-warningDark';
            case value < 60:
                return 'progress-warningLight';
            case value < 80:
                return 'progress-primary';
            default:
                return 'progress-success';
        }
    });

    containerClass = computed(() => {
        if (this.mode === 'temperature') {
            return this.temperatureClass();
        } else if (this.mode === 'default') {
            return 'default';
        } else if (this.mode === 'custom') {
            return 'custom';
        }
        return '';
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['progress']) {
            const newProgress = this.clamp(changes['progress'].currentValue);
            const oldProgress = changes['progress'].firstChange ? 0 : this.clamp(changes['progress'].previousValue);

            if (this.instant) {
                this.animationDuration.set(0);
                this.animatedValue.set(newProgress);
            } else {
                const delta = Math.abs(newProgress - oldProgress);
                const baseDuration = this.ANIMATION_DURATIONS[this.animationSpeed] || this.ANIMATION_DURATIONS.normal;
                const duration = Math.max(50, Math.round((delta / 100) * baseDuration));

                // Set duration first, then update value in next tick for the transition to apply
                this.animationDuration.set(duration);

                // If the timeout is not delayed by 0ms, the transition does not apply and it directly appears at value
                // Timeout OK
                setTimeout(() => {
                    this.animatedValue.set(newProgress);
                }, 0);
            }
        }
    }

    private clamp(value: number): number {
        return Math.min(100, Math.max(0, value));
    }
}
